// En byrå skapar ett konto själv (ADR 0106). Kontot börjar som en
// förhandsvisning: byrån kan titta runt men inte ändra något, och formuläret
// tar inte emot förfrågningar, förrän LeadLedger ger den full åtkomst.
// Ägaren får ett mejl med ett tillfälligt lösenord och loggar in som en
// inbjuden användare.
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../../auth/auth.js';
import { Marke } from '../../layout/ikoner.js';

/** Felet från API:t som en mening: det första valideringsfelet, problemets text, eller en allmän. */
export function registreringsFel(problem: unknown): string {
  const p = problem as { detail?: unknown; errors?: { message?: unknown }[] } | undefined;
  const forsta = p?.errors?.[0]?.message;
  if (typeof forsta === 'string') return forsta;
  if (typeof p?.detail === 'string') return p.detail;
  return 'Det gick inte att skapa kontot. Försök igen om en stund.';
}

function Falt({
  etikett,
  hjalp,
  ...input
}: { etikett: string; hjalp?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className="falt">
      <label className="etikett" htmlFor={id}>
        {etikett}
      </label>
      <input
        id={id}
        className="in"
        required
        {...(hjalp ? { 'aria-describedby': `${id}-hjalp` } : {})}
        {...input}
      />
      {hjalp && (
        <div className="hjalp" id={`${id}-hjalp`}>
          {hjalp}
        </div>
      )}
    </div>
  );
}

export function SkapaKonto() {
  const { api } = useAuth();
  const [falt, setFalt] = useState({ firmName: '', orgNumber: '', ownerName: '', ownerEmail: '' });
  const [webbplats, setWebbplats] = useState('');
  const [vantar, setVantar] = useState(false);
  const [fel, setFel] = useState<string | null>(null);
  const [klar, setKlar] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Skapa konto – LeadLedger';
  }, []);

  const andra = (namn: keyof typeof falt) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFalt((f) => ({ ...f, [namn]: e.target.value }));

  async function skicka(e: FormEvent) {
    e.preventDefault();
    setFel(null);
    setVantar(true);
    try {
      const svar = await api.POST('/v1/public/registrations', {
        body: { ...falt, ...(webbplats ? { website: webbplats } : {}) },
      });
      if (svar.data) setKlar(svar.data.email);
      else setFel(registreringsFel(svar.error));
    } catch {
      setFel('Det gick inte att nå LeadLedger. Kontrollera anslutningen och försök igen.');
    } finally {
      setVantar(false);
    }
  }

  return (
    <main className="inloggning">
      <span className="marke">
        <Marke storlek={34} />
        LeadLedger
      </span>
      <div className="kort inloggning-kort">
        {klar ? (
          <div className="inloggning-form">
            <h1>Kontot är skapat</h1>
            <p role="status">
              Vi har skickat ett mejl till {klar} med ett tillfälligt lösenord. Logga in med det,
              välj ett eget lösenord och koppla en app för engångskoder.
            </p>
            <p>
              Kontot är en förhandsvisning tills ni blir fullständig byråkund. Ni kan titta runt
              direkt, och vi hör av oss för att komma igång.
            </p>
            <Link className="btn btn-p" to="/logga-in">
              Till inloggningen
            </Link>
          </div>
        ) : (
          <form className="inloggning-form" onSubmit={(e) => void skicka(e)}>
            <h1>Skapa konto</h1>
            <p>
              Skapa ett konto för er byrå och titta runt i LeadLedger. Ni blir fullständig byråkund
              när vi har pratat ihop oss.
            </p>
            {fel && (
              <div className="meddelande" role="alert">
                {fel}
              </div>
            )}
            <Falt
              etikett="Byråns namn"
              autoComplete="organization"
              maxLength={120}
              value={falt.firmName}
              onChange={andra('firmName')}
            />
            <Falt
              etikett="Organisationsnummer"
              hjalp="Till exempel 556123-4567."
              inputMode="numeric"
              pattern="\d{6}-?\d{4}"
              value={falt.orgNumber}
              onChange={andra('orgNumber')}
            />
            <Falt
              etikett="Ditt namn"
              autoComplete="name"
              maxLength={100}
              value={falt.ownerName}
              onChange={andra('ownerName')}
            />
            <Falt
              etikett="Din e-post"
              hjalp="Hit skickar vi det tillfälliga lösenordet. Du blir byråns ägare i kontot."
              type="email"
              autoComplete="email"
              maxLength={254}
              value={falt.ownerEmail}
              onChange={andra('ownerEmail')}
            />
            {/* Det dolda fältet. En människa ser det inte och lämnar det tomt. */}
            <div className="dold" aria-hidden="true">
              <label>
                Webbplats
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  value={webbplats}
                  onChange={(e) => setWebbplats(e.target.value)}
                />
              </label>
            </div>
            <p className="hjalp">
              Första gången ni loggar in godkänner ni våra{' '}
              <Link to="/villkor">allmänna villkor</Link> och vårt{' '}
              <Link to="/bitradesavtal">biträdesavtal</Link>.
            </p>
            <button type="submit" className="btn btn-p" disabled={vantar} aria-busy={vantar}>
              {vantar ? 'Skapar kontot …' : 'Skapa konto'}
            </button>
            <Link className="lank-knapp" to="/logga-in">
              Har ni redan ett konto? Logga in
            </Link>
          </form>
        )}
      </div>
    </main>
  );
}
